/**
 * Output-Type-Aware Renderers
 * 
 * Core Principle: Agents do not choose layouts. Output structure determines rendering.
 * 
 * Renderer Selection Priority (strict order):
 * 1. output.sections[] → Insight Renderer
 * 2. output.action_payload[] → Action Renderer  
 * 3. output.structured_json → Data Renderer
 * 4. output.markdown with headings → Insight Renderer (auto-parsed)
 * 5. output.markdown without headings → Document Renderer
 * 6. Fallback → Plain Text Renderer
 * 
 * Only ONE renderer should be active per session.
 */

export { InsightRenderer } from './InsightRenderer';
export { ActionRenderer } from './ActionRenderer';
export { DataRenderer } from './DataRenderer';
export { DocumentRenderer } from './DocumentRenderer';
export { PlainTextRenderer } from './PlainTextRenderer';

/**
 * Parse markdown text into sections based on headings
 * @param {string} markdown - The markdown text to parse
 * @returns {Array} - Array of section objects with key, label, content, type
 */
export const parseMarkdownToSections = (markdown) => {
  if (!markdown || typeof markdown !== 'string') return [];
  
  const sections = [];
  
  // Match markdown headings:
  // 1. ## Heading style
  // 2. **Bold Heading:** style (with colon)
  // 3. **Bold Heading** style (without colon, standalone on line)
  const headingPattern = /(?:^|\n)(#{1,4})\s*([^\n]+)|(?:^|\n)\*\*([^*:\n]+):\*\*\s*|(?:^|\n)\*\*([^*\n]{3,50})\*\*\s*(?:\n|$)/gm;
  const matches = [...markdown.matchAll(headingPattern)];
  
  if (matches.length === 0) return [];
  
  // Find content between headings
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const heading = match[2] || match[3] || match[4]; // ## heading, **Heading:**, or **Heading**
    const start = match.index + match[0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : markdown.length;
    
    let content = markdown.slice(start, end).trim();
    
    // Clean up content - remove artifacts that cause display issues
    content = content
      .replace(/^\*\*\s*$/gm, '')        // Remove lines that are just **
      .replace(/^\*\*$/gm, '')            // Remove standalone **
      .replace(/^---+\s*$/gm, '')         // Remove horizontal rules (---)
      .replace(/^\*\*\*+\s*$/gm, '')      // Remove horizontal rules (***)
      .replace(/^___+\s*$/gm, '')         // Remove horizontal rules (___)
      .replace(/^-{3,}$/gm, '')           // Remove --- lines
      .replace(/^\*{3,}$/gm, '')          // Remove *** lines
      .replace(/^_{3,}$/gm, '')           // Remove ___ lines
      .replace(/\n{3,}/g, '\n\n')         // Collapse multiple newlines
      .trim();
    
    // Only add section if heading and content are both valid
    if (heading && heading.trim() && content && content.length > 0) {
      // Generate clean key from heading
      const key = heading
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
      
      sections.push({
        key: key || `section_${i}`,
        label: heading.trim(),
        content: content,
        type: 'markdown',
        order: i
      });
    }
  }
  
  return sections;
};

/**
 * Detect which renderer to use based on output structure
 * @param {Object} outputData - The session output data
 * @returns {string} - Renderer type: 'insight' | 'action' | 'data' | 'document' | 'plaintext'
 */
export const detectRendererType = (outputData) => {
  if (!outputData) return 'plaintext';
  
  const content = outputData.content || {};
  
  // Priority 1: Sections → Insight Renderer
  const sections = content.sections || [];
  if (sections.length > 0) {
    return 'insight';
  }
  
  // Priority 2: Action Payload → Action Renderer
  const actionPayload = outputData.action_payload || content.action_payload;
  if (actionPayload && (Array.isArray(actionPayload) ? actionPayload.length > 0 : actionPayload.actions?.length > 0)) {
    return 'action';
  }
  
  // Priority 3: Structured JSON → Data Renderer
  const structuredJson = content.structured_json || content.json_data || content.data;
  if (structuredJson && typeof structuredJson === 'object' && Object.keys(structuredJson).length > 0) {
    // Check if it looks like tabular/structured data (has arrays or nested objects)
    const hasArrays = Object.values(structuredJson).some(v => Array.isArray(v));
    const hasNestedObjects = Object.values(structuredJson).some(v => typeof v === 'object' && v !== null && !Array.isArray(v));
    if (hasArrays || hasNestedObjects || Object.keys(structuredJson).length >= 3) {
      return 'data';
    }
  }
  
  // Priority 4: Markdown with headings → Insight Renderer (auto-parse sections)
  const markdown = content.markdown;
  if (markdown && typeof markdown === 'string' && markdown.trim().length > 0) {
    // Check if markdown has multiple headings that can be parsed into sections
    const hasHeadings = /(?:^|\n)#{1,4}\s+[^\n]+/m.test(markdown) || 
                        /\*\*[^*:]+:\*\*/m.test(markdown);
    const parsedSections = hasHeadings ? parseMarkdownToSections(markdown) : [];
    
    if (parsedSections.length >= 2) {
      // Has enough sections to use Insight Renderer
      return 'insight';
    }
    
    // Single or no sections - use Document Renderer
    return 'document';
  }
  
  // Priority 5: Fallback → Plain Text
  return 'plaintext';
};

/**
 * Get renderer label for UI display
 */
export const getRendererLabel = (type) => {
  const labels = {
    insight: 'Analysis Result',
    action: 'Recommended Actions',
    data: 'Data Report',
    document: 'Document',
    plaintext: 'Output'
  };
  return labels[type] || 'Output';
};
